import { BasicPage } from "../components/BasicPage";
import { useAuth, apiFetch } from "../hooks/useAuth";
import { 
    Card, 
    Typography, 
    CardContent, 
    Box, 
    Grid, 
    Paper,
    LinearProgress,
    Chip,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText
} from "@mui/material/";
import { 
    BarChart, 
    ShoppingCart, 
    Book, 
    Euro, 
    TrendingUp,
    CheckCircle,
    Schedule,
    Info,
    School,
    LocalLibrary
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from 'dayjs';
import 'dayjs/locale/de';

export const WelcomeSchoeningh = () => {
    const { user, accountID } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [stats, setStats] = useState({
        yearlySpend: 0,
        yearlySpend2024: 0,
        openOrders: 0,
        openOrders2024: 0,
        totalOrders: 0,
        activeSubscriptions: 0,
        pendingDeliveries: 0,
        recentOrders: [],
        oldestOrderDate: null
    });

    const numberFormat = (value) =>
        new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR'
        }).format(value);

    useEffect(() => {
        const fetchStats = async () => {
            if (!accountID || accountID.length === 0) {
                setError("Keine Account-ID ausgewählt");
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError('');

                // Use all account IDs for comprehensive statistics
                const accountIDs = Array.isArray(accountID) ? accountID : [accountID];

                // Fetch orders, subscriptions, and invoices for all account IDs
                const [ordersResponse, abosResponse, invoicesResponse] = await Promise.all([
                    apiFetch(`/app/orders/${accountIDs.join(',')}`),
                    apiFetch(`/app/abos/${accountIDs.join(',')}`),
                    apiFetch(`/app/invoices/${accountIDs.join(',')}`)
                ]);

                const ordersData = await ordersResponse.json();
                const abosData = await abosResponse.json();
                const invoicesData = await invoicesResponse.json();

                // Debug: Log subscription data to see what status values exist
                console.log('Abos Data:', abosData);
                console.log('Available status values:', [...new Set(abosData.map(abo => abo.Status))]);

                // Calculate statistics
                const currentYear = dayjs().year();
                
                // Helper function to safely parse dates
                const parseOrderDate = (order) => {
                    // Try different date formats that might come from backend
                    const dateStr = order.Bestelldatum1 || order.Bestelldatum;
                    if (!dateStr) return null;
                    
                    // Handle different date formats
                    let parsedDate = dayjs(dateStr);
                    if (!parsedDate.isValid()) {
                        // Try parsing as DD.MM.YYYY format
                        parsedDate = dayjs(dateStr, 'DD.MM.YYYY');
                    }
                    return parsedDate.isValid() ? parsedDate : null;
                };
                
                // Calculate financial data from invoice data
                
                // Calculate total spending from invoices for current year
                const yearlySpend = invoicesData
                    .filter(invoice => {
                        const invoiceYear = dayjs(invoice.Rechnungsdatum).year();
                        return invoiceYear === currentYear;
                    })
                    .reduce((sum, invoice) => sum + (parseFloat(invoice.Betrag) || 0), 0);
                
                // Calculate total spending from invoices for 2024
                const yearlySpend2024 = invoicesData
                    .filter(invoice => {
                        const invoiceYear = dayjs(invoice.Rechnungsdatum).year();
                        return invoiceYear === 2024;
                    })
                    .reduce((sum, invoice) => sum + (parseFloat(invoice.Betrag) || 0), 0);

                // Bestellungen in Bearbeitung (nur bestellt)
                const ordersInProgress = ordersData.filter(order => 
                    order.Bestellstatus === 'bestellt'
                ).length;
                
                // Ausstehende Abrechnungen (geliefert, nicht abgerechnet)
                const pendingInvoices = ordersData.filter(order => 
                    order.Bestellstatus === 'geliefert' && order.Belegart !== 'Rechnung'
                ).length;
                
                // Gesamte offene Bestellungen (beide zusammen)
                const openOrders = ordersInProgress + pendingInvoices;
                
                const ordersInProgress2024 = ordersData.filter(order => {
                    const orderDate = parseOrderDate(order);
                    return orderDate && orderDate.year() === 2024 &&
                        order.Bestellstatus === 'bestellt';
                }).length;
                
                const pendingInvoices2024 = ordersData.filter(order => {
                    const orderDate = parseOrderDate(order);
                    return orderDate && orderDate.year() === 2024 &&
                        order.Bestellstatus === 'geliefert' && order.Belegart !== 'Rechnung';
                }).length;
                
                const openOrders2024 = ordersInProgress2024 + pendingInvoices2024;

                const pendingDeliveries = ordersData.filter(order => 
                    order.Bestellstatus === 'bestellt' && 
                    order.StatusDetail !== 'berechnet'
                ).length;

                const activeSubscriptions = abosData.length;
                
                console.log('Total abos:', abosData.length);
                console.log('Active subscriptions count:', activeSubscriptions);
                console.log('Orders data sample:', ordersData.slice(0, 3));
                console.log('Invoices data sample:', invoicesData.slice(0, 3));
                console.log('Financial calculation (from invoices):', {
                    currentYear,
                    yearlySpend,
                    yearlySpend2024,
                    totalOrders: ordersData.length,
                    totalInvoices: invoicesData.length,
                    currentYearInvoices: invoicesData.filter(invoice => dayjs(invoice.Rechnungsdatum).year() === currentYear).length,
                    year2024Invoices: invoicesData.filter(invoice => dayjs(invoice.Rechnungsdatum).year() === 2024).length
                });

                const recentOrders = ordersData
                    .sort((a, b) => {
                        const dateA = parseOrderDate(a);
                        const dateB = parseOrderDate(b);
                        if (!dateA && !dateB) return 0;
                        if (!dateA) return 1;
                        if (!dateB) return -1;
                        return dateB.valueOf() - dateA.valueOf();
                    })
                    .slice(0, 5);

                // Find oldest order date
                const oldestOrderDate = ordersData.reduce((oldest, order) => {
                    const orderDate = parseOrderDate(order);
                    if (!orderDate) return oldest;
                    if (!oldest || orderDate.isBefore(oldest)) {
                        return orderDate;
                    }
                    return oldest;
                }, null);

                setStats({
                    yearlySpend,
                    yearlySpend2024,
                    openOrders,
                    openOrders2024,
                    ordersInProgress,
                    ordersInProgress2024,
                    pendingInvoices,
                    pendingInvoices2024,
                    totalOrders: ordersData.length,
                    activeSubscriptions,
                    pendingDeliveries,
                    recentOrders,
                    oldestOrderDate
                });

            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, [accountID]);

    if (loading) {
        return (
            <BasicPage title="Willkommen bei Schöningh" icon={<LocalLibrary />}>
                <Box sx={{ width: '100%', mt: 4 }}>
                    <LinearProgress />
                    <Typography variant="body1" sx={{ mt: 2, textAlign: 'center' }}>
                        Lade Statistiken...
                    </Typography>
                </Box>
            </BasicPage>
        );
    }

    return (
        <BasicPage title="Willkommen bei Schöningh" icon={<LocalLibrary />}>
            <Grid container spacing={3} sx={{ mt: 2 }}>
                {/* Tool Information */}
                <Grid item xs={12} md={8}>
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h5" gutterBottom sx={{ color: '#3caf82', fontWeight: 'bold' }}>
                                📚 Schöningh Fachbuchportal
                            </Typography>
                            <Typography variant="body1" paragraph>
                                Willkommen im digitalen Fachbuchportal von Schöningh. Hier können Sie:
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemIcon>
                                        <Book color="primary" />
                                    </ListItemIcon>
                                    <ListItemText 
                                        primary="Fachbücher und wissenschaftliche Literatur bestellen"
                                        secondary="Umfangreiches Sortiment an Fachliteratur für Forschung und Lehre"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <ShoppingCart color="primary" />
                                    </ListItemIcon>
                                    <ListItemText 
                                        primary="Abonnements verwalten"
                                        secondary="Zeitschriften und Reihen abonnieren und verwalten"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <TrendingUp color="primary" />
                                    </ListItemIcon>
                                    <ListItemText 
                                        primary="Bestellungen und Lieferungen verfolgen"
                                        secondary="Echtzeit-Updates zu Ihren Bestellungen und Lieferungen"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <School color="primary" />
                                    </ListItemIcon>
                                    <ListItemText 
                                        primary="Kostenstellen-basierte Bestellungen"
                                        secondary="Bestellungen nach Kostenstellen organisieren und verwalten"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <Euro color="primary" />
                                    </ListItemIcon>
                                    <ListItemText 
                                        primary="Rechnungsarchiv. Alle Ihre Rechnungen seit 2023 zum Download"
                                        secondary="Vollständiges Archiv aller Rechnungen mit Suchfunktion"
                                    />
                                </ListItem>
                            </List>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Quick Stats */}
                <Grid item xs={12} md={4}>
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                📊 Schnellübersicht
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                    <Typography variant="body2">Jahresausgaben:</Typography>
                                    <Typography variant="body2" fontWeight="bold">
                                        {numberFormat(stats.yearlySpend)}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="body2">Bestellungen in Bearbeitung:</Typography>
                                    <Typography variant="body2" fontWeight="bold">
                                        {stats.ordersInProgress}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                    <Typography variant="body2">Ausstehende Abrechnungen:</Typography>
                                    <Typography variant="body2" fontWeight="bold">
                                        {stats.pendingInvoices}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                    <Typography variant="body2">Zeitschriften und Fortsetzungen:</Typography>
                                    <Typography variant="body2" fontWeight="bold">
                                        {stats.activeSubscriptions}
                                    </Typography>
                                </Box>
                            </Box>
                            
                            <Divider sx={{ my: 2 }} />
                            
                            <Typography variant="h6" gutterBottom>
                                ⚡ Schnellzugriff
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                                <Chip 
                                    label="Neue Bestellung"
                                    color="primary"
                                    sx={{ mb: 1, mr: 1, cursor: 'pointer' }}
                                    onClick={() => navigate('/web/orderservice')}
                                />
                                <Chip
                                    label="Meta-Suche"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ mb: 1, mr: 1, cursor: 'pointer' }}
                                    onClick={() => navigate('/web/searchmeta')}
                                />
                                <Chip 
                                    label="Abonnements"
                                    color="secondary"
                                    sx={{ mb: 1, mr: 1, cursor: 'pointer' }}
                                    onClick={() => navigate('/web/abonnements/abostatus')}
                                />
                                <Chip 
                                    label="Bestellstatus"
                                    color="info"
                                    sx={{ mb: 1, mr: 1, cursor: 'pointer' }}
                                    onClick={() => navigate('/web/orderstatus')}
                                />
                                <Chip 
                                    label="Rechnungsarchiv"
                                    color="warning"
                                    sx={{ mb: 1, mr: 1, cursor: 'pointer' }}
                                    onClick={() => navigate('/web/rechnungsarchiv')}
                                />
                                <Chip 
                                    label="Webshop"
                                    color="success"
                                    sx={{ mb: 1, mr: 1, cursor: 'pointer' }}
                                    onClick={() => window.open('https://fachmedien.schoeningh-buch.de/', '_blank')}
                                />
                                <Chip 
                                    label="Kontakt"
                                    color="default"
                                    sx={{ mb: 1, mr: 1, cursor: 'pointer' }}
                                    onClick={() => navigate('/web/contact')}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Detailed Statistics */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ color: '#3caf82', fontWeight: 'bold' }}>
                                📈 Bestellstatistiken
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Paper sx={{ 
                                        p: 2.5, 
                                        textAlign: 'center', 
                                        bgcolor: '#fafafa',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 1,
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                            bgcolor: '#f5f5f5',
                                            borderColor: '#d0d0d0'
                                        }
                                    }}>
                                        <Typography variant="h4" color="text.primary" sx={{ fontWeight: 500 }}>
                                            {stats.totalOrders}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                                            Gesamtbestellungen
                                        </Typography>
                                        {stats.oldestOrderDate && (
                                            <Typography variant="caption" sx={{ color: '#999', mt: 0.5, display: 'block' }}>
                                                seit {stats.oldestOrderDate.format('DD.MM.YYYY')}
                                            </Typography>
                                        )}
                                    </Paper>
                                </Grid>
                                <Grid item xs={6}>
                                    <Paper sx={{ 
                                        p: 2.5, 
                                        textAlign: 'center', 
                                        bgcolor: '#f8f9fa',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 1,
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                            bgcolor: '#f0f0f0',
                                            borderColor: '#d0d0d0'
                                        }
                                    }}>
                                        <Typography variant="h4" color="text.primary" sx={{ fontWeight: 500 }}>
                                            {stats.activeSubscriptions}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                                            Zeitschriften und Fortsetzungen
                                        </Typography>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ color: '#3caf82', fontWeight: 'bold' }}>
                                💰 Finanzübersicht
                            </Typography>
                            
                            <Grid container spacing={2}>
                                {/* Current Year Spending */}
                                <Grid item xs={6}>
                                    <Paper sx={{ 
                                        p: 2.5, 
                                        textAlign: 'center', 
                                        bgcolor: '#fafafa', 
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 1,
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                            bgcolor: '#f5f5f5',
                                            borderColor: '#d0d0d0'
                                        }
                                    }}>
                                        <Typography variant="h4" color="text.primary" sx={{ fontWeight: 500 }}>
                                            {numberFormat(stats.yearlySpend)}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                                            Ausgaben {dayjs().year()}
                                        </Typography>
                                    </Paper>
                                </Grid>
                                
                                {/* 2024 Spending */}
                                <Grid item xs={6}>
                                    <Paper sx={{ 
                                        p: 2.5, 
                                        textAlign: 'center', 
                                        bgcolor: '#f8f9fa', 
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 1,
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                            bgcolor: '#f0f0f0',
                                            borderColor: '#d0d0d0'
                                        }
                                    }}>
                                        <Typography variant="h4" color="text.primary" sx={{ fontWeight: 500 }}>
                                            {numberFormat(stats.yearlySpend2024)}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                                            Ausgaben 2024
                                        </Typography>
                                    </Paper>
                                </Grid>
                            </Grid>
                            

                        </CardContent>
                    </Card>
                </Grid>

                {/* Recent Orders */}
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                🕒 Letzte Bestellungen
                            </Typography>
                            {stats.recentOrders.length > 0 ? (
                                <List>
                                    {stats.recentOrders.map((order, index) => (
                                        <Box key={index}>
                                            <ListItem>
                                                <ListItemIcon>
                                                    <CheckCircle color="primary" />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={order.Titel || 'Unbekannter Titel'}
                                                    secondary={
                                                        <>
                                                            <span style={{ display: 'block' }}>
                                                                Bestelldatum: {dayjs(order.Bestelldatum1).format('DD.MM.YYYY')}
                                                            </span>
                                                            <span style={{ display: 'block' }}>
                                                                Status: {order.Bestellstatus} • {numberFormat(order.Gesamtbetrag)}
                                                            </span>
                                                        </>
                                                    }
                                                />
                                                <Chip 
                                                    label={order.Belegart === 'Rechnung' ? 'berechnet' : order.Bestellstatus}
                                                    color={
                                                        order.Belegart === 'Rechnung' ? 'success' :
                                                        order.Bestellstatus === 'geliefert' ? 'info' :
                                                        order.Bestellstatus === 'bestellt' ? 'warning' : 'default'
                                                    }
                                                    size="small"
                                                />
                                            </ListItem>
                                            {index < stats.recentOrders.length - 1 && <Divider />}
                                        </Box>
                                    ))}
                                </List>
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                    Keine Bestellungen gefunden
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>


                {/* Error Display */}
                {error && (
                    <Grid item xs={12}>
                        <Card sx={{ bgcolor: '#ffebee' }}>
                            <CardContent>
                                <Typography variant="body1" color="error">
                                    ❌ Fehler beim Laden der Daten: {error}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
            </Grid>
        </BasicPage>
    );
}; 